import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CartItemModel, CourseModel } from '../../core/models';
import { cartBookingItems, cartSubtotal, clusterValidationError, expandCluster, includedCourseIds } from '../../core/cluster-pricing';

@Injectable({ providedIn: 'root' })
export class CartService {
  lastError = '';
  lastNotice = '';
  private readonly cartKey = 'ecb_cart_items';
  cartItems$ = new BehaviorSubject<CartItemModel[]>(this.getItems());

  getItems(): CartItemModel[] {
    try {
      const saved: CartItemModel[] = JSON.parse(localStorage.getItem(this.cartKey) || '[]');
      if (!Array.isArray(saved)) return [];
      const items = saved.filter(item => item?.course).flatMap(item =>
        item.course.isCluster && item.course.clusterCourses?.length ? expandCluster(item.course) : [item]);
      return items.map(item => {
        if (!item.cluster) return { ...item, clusterDiscount: undefined };
        const group = items.filter(line => line.cluster?.courseId === item.cluster.courseId);
        const ids = new Set(group.map(line => line.course.courseId));
        const memberIds = includedCourseIds(item.cluster);
        if (memberIds.length !== group.length || memberIds.some(id => !ids.has(id))) {
          return { ...item, cluster: undefined, clusterDiscount: undefined };
        }
        const fresh = expandCluster(item.cluster).find(line => line.course.courseId === item.course.courseId);
        return { ...item, clusterDiscount: fresh?.clusterDiscount || 0 };
      });
    } catch { return []; }
  }

  addCourse(course: CourseModel): boolean {
    this.lastError = '';
    this.lastNotice = '';
    const items = this.getItems();
    if (course.isCluster) {
      if (clusterValidationError(course) || !course.isActive) {
        this.lastError = 'This cluster is currently unavailable. You can still choose available individual units.';
        return false;
      }
      if (this.isInCart(course.courseId)) { this.lastError = 'This cluster is already in your cart.'; return false; }
      const memberIds = new Set(includedCourseIds(course));
      if (items.some(item => memberIds.has(item.course.courseId) && item.cluster)) {
        this.lastError = 'A linked unit is already part of another cluster. Remove that cluster before adding this one.';
        return false;
      }
      if (!this.sameCurrency(items, course.currencyCode)) return false;
      this.save([...items.filter(item => !memberIds.has(item.course.courseId)), ...expandCluster(course)]);
      this.lastNotice = 'All ' + memberIds.size + ' included courses added. The cluster discount is applied.';
      return true;
    }
    if (items.some(item => item.course.courseId === course.courseId)) {
      this.lastError = 'This unit is already in your cart, individually or as part of a cluster.';
      return false;
    }
    if (!course.isActive) { this.lastError = 'This unit is currently unavailable.'; return false; }
    if (!this.sameCurrency(items, course.currencyCode)) return false;
    this.save([...items, { course, quantity: 1 }]);
    this.lastNotice = 'Individual unit added at its standard price.';
    return true;
  }

  addIndividualUnits(clusterId: number, courses: CourseModel[]): boolean {
    this.lastError = '';
    this.lastNotice = '';
    if (!courses.length || courses.some(course => !course.isActive || course.isCluster)) {
      this.lastError = 'Choose at least one available individual unit.';
      return false;
    }
    const items = this.getItems().filter(item => item.cluster?.courseId !== clusterId);
    if (!this.sameCurrency([...items, ...courses.map(course => ({ course, quantity: 1 }))], courses[0].currencyCode)) return false;
    if (courses.some(course => items.some(item => item.course.courseId === course.courseId && item.cluster))) {
      this.lastError = 'A selected unit is already part of another cluster.';
      return false;
    }
    const selected = new Map(items.map(item => [item.course.courseId, item]));
    courses.forEach(course => selected.set(course.courseId, { course, quantity: 1 }));
    this.save([...selected.values()]);
    this.lastNotice = courses.length + ' individual ' + (courses.length === 1 ? 'unit' : 'units') + ' added at standard pricing.';
    return true;
  }

  selectClusterForCourse(course: CourseModel, currentCourseId: number): boolean {
    this.lastError = '';
    this.lastNotice = '';
    if (!course.isCluster || !course.isActive || clusterValidationError(course) ||
        !includedCourseIds(course).includes(Number(currentCourseId))) {
      this.lastError = 'This discount is no longer available for the selected course.';
      return false;
    }
    const items = this.getItems();
    const previous = items.find(item => item.course.courseId === currentCourseId)?.cluster;
    if (!previous || previous.courseId === course.courseId) return this.addCourse(course);
    const remaining = items.filter(item => item.cluster?.courseId !== previous.courseId);
    const ids = new Set(includedCourseIds(course));
    if (remaining.some(item => item.cluster && ids.has(item.course.courseId))) {
      this.lastError = 'This offer overlaps another cluster in your cart. Remove that cluster before switching.';
      return false;
    }
    if (!this.sameCurrency(remaining, course.currencyCode)) return false;
    this.save([...remaining.filter(item => !ids.has(item.course.courseId)), ...expandCluster(course)]);
    this.lastNotice = 'Switched to ' + course.courseName + '. All linked courses and the discount have been updated.';
    return true;
  }

  /** Returns true only when the course is selected at its normal individual price. */
  isIndividualInCart(courseId: number): boolean {
    return this.getItems().some(item =>
      item.course.courseId === Number(courseId) && !item.cluster
    );
  }

  /** Returns true only when the specified discounted cluster is selected. */
  isClusterInCart(clusterId: number): boolean {
    return this.getItems().some(item =>
      item.cluster?.courseId === Number(clusterId)
    );
  }

  /**
   * Selects the current course by itself at its standard/original price.
   * If that course is currently included in a cluster, the complete cluster
   * is removed first and the individual course is added instead.
   */
  selectIndividualCourse(course: CourseModel): boolean {
    this.lastError = '';
    this.lastNotice = '';

    if (!course || course.courseId <= 0 || !course.isActive) {
      this.lastError = 'This course is currently unavailable.';
      return false;
    }

    // The caller supplies an individual-shaped model even when the database row
    // is also the master of one or more cluster offers.
    const individual: CourseModel = {
      ...course,
      isCluster: false,
      clusterAnchorCourseId: undefined,
      clusterCourseIds: [],
      clusterCourses: [],
      availableClusters: course.availableClusters || []
    };

    let items = this.getItems();

    if (items.some(item => item.course.courseId === individual.courseId && !item.cluster)) {
      this.lastError = 'This course is already in your cart individually.';
      return false;
    }

    // Find every selected cluster containing this course and remove the whole
    // cluster group before adding the individual selection.
    const clusterIdsToRemove = new Set<number>();
    items.forEach(item => {
      if (item.cluster && includedCourseIds(item.cluster).includes(individual.courseId)) {
        clusterIdsToRemove.add(item.cluster.courseId);
      }
    });

    if (clusterIdsToRemove.size) {
      items = items.filter(item =>
        !item.cluster || !clusterIdsToRemove.has(item.cluster.courseId)
      );
    }

    if (!this.sameCurrency(items, individual.currencyCode)) {
      return false;
    }

    this.save([...items, { course: individual, quantity: 1 }]);
    this.lastNotice = clusterIdsToRemove.size
      ? 'Cluster selection replaced with this course at its individual price.'
      : 'Individual course added at its standard price.';
    return true;
  }

  isInCart(courseId: number): boolean {
    return this.isIndividualInCart(courseId) || this.isClusterInCart(courseId);
  }

  removeCourse(courseId: number): void {
    const items = this.getItems();
    const groupId = items.find(item => item.course.courseId === courseId)?.cluster?.courseId;
    this.save(items.filter(item => item.course.courseId !== courseId && item.cluster?.courseId !== courseId).map(item =>
      groupId && item.cluster?.courseId === groupId ? { ...item, cluster: undefined, clusterDiscount: undefined } : item));
    this.lastNotice = groupId ? 'Unit removed. The remaining units now use individual prices because the cluster is incomplete.' : 'Selection removed.';
  }

  clear(): void { this.save([]); }

  refreshCourses(courses: CourseModel[]): void {
    const current = this.getItems();
    this.save(cartBookingItems(current).flatMap(item => {
      const course = courses.find(value => value.courseId === item.course.courseId) || item.course;
      return course.isCluster ? expandCluster(course).map(line => ({
        ...line,
        course: { ...line.course, courseTimes: courses.find(value => value.courseId === line.course.courseId)?.courseTimes
          ?? current.find(value => value.course.courseId === line.course.courseId)?.course.courseTimes
          ?? line.course.courseTimes }
      })) : [{ course, quantity: 1 }];
    }));
  }

  // Loading member schedules must preserve cluster membership and allocated discounts.
  refreshCourseSessions(courses: CourseModel[]): void {
    this.save(this.getItems().map(item => {
      const current = courses.find(course => course.courseId === item.course.courseId);
      return current ? { ...item, course: { ...item.course, isActive: current.isActive,
        courseTimes: current.courseTimes || [] } } : item;
    }));
  }

  getCount(): number { return this.getItems().length; }
  getSubtotal(): number { return cartSubtotal(this.getItems()); }

  private sameCurrency(items: CartItemModel[], currency: string): boolean {
    if (items.some(item => (item.course.currencyCode || 'AUD') !== (currency || 'AUD'))) {
      this.lastError = 'Please book courses in different currencies separately.';
      return false;
    }
    return true;
  }

  private save(items: CartItemModel[]): void {
    localStorage.setItem(this.cartKey, JSON.stringify(items));
    this.cartItems$.next(items);
  }
}
